<script lang="ts">
	import { format, locale } from 'svelte-i18n';
	import { save_profile } from 'src/helpers/profile_helpers';
	import { profile_data, user_data } from 'src/stores/account/user';
	import { navigateToShop } from '../Lists/list-helpers';
	import { ButtonV2 } from '@Shelly/shelly-design-system';
	import { getPromoExpiryText } from 'src/helpers/promo_helpers';
	import { PROMOTIONS } from 'src/constants';

	// Custom Promo Modal components
	import PromoModal from 'src/components/Promotions/organisms/PromoModal.svelte';
	import PromoModalBanner from 'src/components/Promotions/molecules/PromoModalBanner.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { createEventDispatcher } from 'svelte';

	export let showModal = false;

	const dispatch = createEventDispatcher();

	const onCloseModal = (fromShopButton = false) => {
		if (showModal) {
			showModal = false;
			// Mark as seen when closing
			if (!$profile_data?.promotions?.thirtyMillionDevices) {
				save_profile('promotions', {
					...$profile_data?.promotions,
					thirtyMillionDevices: new Date().getTime(),
				});
			}
			if (!fromShopButton) {
				const thm = { ...$profile_data?.aep?.thm };
				thm.closeOnPromotionModal = (thm?.closeOnPromotionModal || 0) + 1;
				save_profile('aep', { ...$profile_data.aep, thm });
			}
			dispatch('close');
		}
	};

	const handleSaveForLater = () => {
		onCloseModal();
	};

	const handleOpenShop = () => {
		onCloseModal(true);
		const thm = { ...$profile_data?.aep?.thm };
		thm.clickOnShopNowButton = (thm?.clickOnShopNowButton || 0) + 1;
		save_profile('aep', { ...$profile_data.aep, thm });
		if ($user_data?.timezone.startsWith('America/')) {
			navigateToShop(
				'https://us.shelly.com/pages/30m-devices?utm_source=shellyapp&utm_medium=referral&utm_campaign=30M',
			);
		} else {
			navigateToShop(
				`https://www.shelly.com/${['de', 'fr', 'it'].includes($locale) ? `${$locale}/` : ''}pages/30m-devices?utm_source=shellyapp&utm_medium=referral&utm_campaign=30M`,
			);
		}
	};

	$: expiryText = $format(
		getPromoExpiryText(PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE)?.key,
		getPromoExpiryText(PROMOTIONS.THIRTY_MILLION_DEVICES.END_DATE)?.object,
	);

	$: image = $user_data?.timezone.startsWith('America/')
		? './images/30m_us.png'
		: `./images/30m_${['de', 'fr', 'it'].includes($locale) ? $locale : 'default'}.png`;
</script>

<PromoModal
	{showModal}
	title={$format('heading_30_million_devices', { default: '30 Million Smart Decisions' })}
	closeAriaLabel={$format('button_close', { default: 'Close' })}
	on:close={handleSaveForLater}
	backdropClose
>
	<!-- Banner with Active badge and Expiry -->
	<PromoModalBanner
		{image}
		alt={$format('heading_30_million_devices', { default: '30 Million Smart Decisions' })}
		activeBadgeLabel={$format('label_active', { default: 'Active' })}
		{expiryText}
	/>

	<!-- Content Section -->
	<div class="content-section">
		<div class="content-text">
			<p class="content-paragraph text-300">
				{$format('text_30_million_intro', {
					default:
						"To celebrate 30 million Shelly devices powering the world, we're scaling our rewards to match your ambition. The more you automate, the more you save.",
				})}
			</p>
			<p class="content-paragraph text-300">
				{$format('text_30_million_calculation', {
					default: "It's a simple calculation: Buy more. Save more!",
				})}
			</p>
			<ul class="discount-list">
				<li class="text-300">
					{@html $format('text_30_million_tier_1', {
						default: 'Buy over {currency} and get <span class="text-bold">10% OFF</span>',
						values: {
							currency: $user_data?.timezone.startsWith('America/') ? '$100' : '100 €',
						},
					})}
				</li>
				<li class="text-300">
					{@html $format('text_30_million_tier_2', {
						default: 'Buy over {currency} and get <span class="text-bold">20% OFF</span>',
						values: {
							currency: $user_data?.timezone.startsWith('America/') ? '$200' : '200 €',
						},
					})}
				</li>
				<li class="text-300">
					{@html $format('text_30_million_tier_3', {
						default: 'Buy over {currency} and get <span class="text-bold">30% OFF</span>',
						values: {
							currency: $user_data?.timezone.startsWith('America/') ? '$300' : '300 €',
						},
					})}
				</li>
			</ul>
		</div>
		<!-- Actions -->
	</div>

	<svelte:fragment slot="footer">
		<div class="width-full d-flex justify-content-end gap-s">
			<ButtonV2
				variant="secondary"
				on:click={handleSaveForLater}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			>
				{capFirstChar($format('button_maybe_later', { default: 'Maybe later' }))}
			</ButtonV2>
			<ButtonV2
				variant="primary"
				on:click={handleOpenShop}
				icon="fas fa-shop"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			>
				{$format('button_shop_now', { default: 'Shop Now' })}
			</ButtonV2>
		</div>
	</svelte:fragment>
</PromoModal>

<style>
	.content-section {
		display: flex;
		flex-direction: column;
		gap: 24px;
		padding: 24px 24px 0px 24px;
	}

	.content-text {
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 19px;
		color: var(--color-white, #ffffff);
	}

	.content-paragraph {
		margin: 0 0 10px 0;
	}

	.discount-list {
		list-style: disc;
		padding-left: 24px;
		margin: 0;
	}

	.discount-list li {
		margin-bottom: 0;
		line-height: 19px;
	}

	:global(.discount-list .text-bold) {
		font-weight: 700;
	}

	.faq-link {
		display: flex;
		align-items: center;
		gap: 4px;
		min-width: 168px;
		height: 44px;
		padding: 0;
		margin: 0;
		background: transparent;
		border: none;
		cursor: pointer;
	}

	.faq-link__text {
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 400;
		line-height: 19px;
		color: var(--color-primary, #2491ff);
	}

	.faq-link:hover .faq-link__text {
		text-decoration: underline;
	}

	.button-group {
		display: flex;
		gap: 8px;
	}

	.secondary-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 45px;
		padding: 0 24px;
		background: transparent;
		border: 1px solid rgba(255, 255, 255, 0.2);
		border-radius: 200px;
		cursor: pointer;
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 500;
		line-height: normal;
		color: var(--color-white, #ffffff);
		white-space: nowrap;
		transition: border-color 0.15s ease;
	}

	.secondary-btn:hover {
		border-color: rgba(255, 255, 255, 0.4);
	}

	.secondary-btn:focus-visible {
		outline: 2px solid var(--color-primary, #2491ff);
		outline-offset: 2px;
	}

	.primary-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		height: 44px;
		padding: 12px 16px;
		background: var(--color-white, #ffffff);
		border: none;
		border-radius: 200px;
		cursor: pointer;
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 700;
		line-height: 19px;
		color: var(--color-0, #1b1d21);
		white-space: nowrap;
		transition: opacity 0.15s ease;
	}

	.primary-btn:hover {
		opacity: 0.9;
	}

	.primary-btn:focus-visible {
		outline: 2px solid var(--color-primary, #2491ff);
		outline-offset: 2px;
	}

	:global(.primary-btn .icon) {
		color: var(--color-primary, #2491ff) !important;
	}

	/* Mobile responsive */
	@media (max-width: 639px) {
		.actions-container {
			flex-direction: column;
			align-items: stretch;
			height: auto;
			gap: 12px;
		}

		.faq-link {
			justify-content: center;
			order: 2;
			min-width: unset;
		}

		.button-group {
			flex-direction: column;
			order: 1;
		}

		.secondary-btn,
		.primary-btn {
			width: 100%;
		}
	}
</style>
